import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Building, Crown, Users, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { API_URL } from '../../utils/apiConfig';

// Apple-inspired design system
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

const CompanySetupStep = ({ onNext, onSave }) => {
  const { user, completeOnboardingStep } = useAuth();
  const { completeStep, updateUserData, userData } = useOnboarding();
  
  const [formData, setFormData] = useState({
    companyName: userData.companyName || '',
    companySize: userData.companySize || '',
    industry: userData.industry || ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const companySizeOptions = [
    { value: '1-10', label: '1-10 employees', icon: '👤' },
    { value: '11-50', label: '11-50 employees', icon: '👥' },
    { value: '51-200', label: '51-200 employees', icon: '🏢' },
    { value: '201-500', label: '201-500 employees', icon: '🏭' },
    { value: '500+', label: '500+ employees', icon: '🌆' }
  ];

  const industryOptions = [
    { value: 'Technology', label: 'Technology & Software', icon: '💻' },
    { value: 'Financial', label: 'Financial Services', icon: '💰' },
    { value: 'Healthcare', label: 'Healthcare & Medical', icon: '🏥' },
    { value: 'Legal', label: 'Legal Services', icon: '⚖️' },
    { value: 'Education', label: 'Education', icon: '🎓' },
    { value: 'Real Estate', label: 'Real Estate', icon: '🏠' },
    { value: 'Manufacturing', label: 'Manufacturing', icon: '🏭' },
    { value: 'Retail', label: 'Retail & E-commerce', icon: '🛍️' },
    { value: 'Consulting', label: 'Consulting', icon: '📊' },
    { value: 'Other', label: 'Other', icon: '🔧' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing/selecting
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters';
    }
    
    if (!formData.companySize) {
      newErrors.companySize = 'Please select your company size';
    }
    
    if (!formData.industry) {
      newErrors.industry = 'Please select your industry';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSave = useCallback(async () => {
    if (validateForm()) {
      setIsLoading(true);
      setApiError('');
      
      try {
        // First update user data in context
        await updateUserData(formData);
        
        // Call backend API to setup company workspace
        const response = await fetch(`${API_URL}/users/setup-company-workspace`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to setup company workspace');
        }

        const result = await response.json();
        console.log('✅ Company workspace setup completed:', result);
        
        // Complete this onboarding step
        await completeOnboardingStep('companySetup');
        
        // Mark step as complete in onboarding context
        completeStep('companySetup');
        
        return true; // Success
      } catch (error) {
        console.error('❌ Error setting up company workspace:', error);
        setApiError(error.message || 'Failed to save company information');
        return false; // Failed
      } finally {
        setIsLoading(false);
      }
    }
    return false; // Validation failed
  }, [formData, validateForm, updateUserData, completeOnboardingStep, completeStep]);

  // Expose save function to parent via onSave prop
  React.useEffect(() => {
    if (onSave) {
      onSave(handleSave);
    }
  }, [onSave, handleSave]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      style={{ 
        padding: '40px',
        maxWidth: '600px',
        margin: '0 auto',
        ...fontStyles
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <motion.div
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2 }}
          style={{
            width: '80px',
            height: '80px',
            background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
            borderRadius: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: `0 8px 32px ${colors.accentLight}`
          }}
        >
          <Building size={36} color="white" />
        </motion.div>
        
        <h2 style={{
          fontSize: '32px',
          color: colors.gray900,
          margin: '0 0 12px',
          ...fontStyles.heading
        }}>
          Set Up Your Company
        </h2>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '16px'
        }}>
          <Crown size={20} color={colors.warning} />
          <span style={{
            color: colors.warning,
            fontSize: '16px',
            fontWeight: 600
          }}>
            Company Administrator Account
          </span>
        </div>
        
        <p style={{
          fontSize: '18px',
          color: colors.gray500,
          lineHeight: '1.5',
          ...fontStyles.body
        }}>
          As a company administrator, you'll manage your organization's workspace, team members, and billing settings.
        </p>
      </div>

      {/* Admin Privileges Info */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        style={{
          background: colors.successLight,
          border: `1px solid ${colors.success}`,
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '32px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <CheckCircle size={20} color={colors.success} />
          <span style={{
            color: colors.success,
            fontSize: '16px',
            fontWeight: 600
          }}>
            Administrator Privileges
          </span>
        </div>
        <ul style={{
          color: colors.gray700,
          fontSize: '14px',
          lineHeight: '1.5',
          margin: 0,
          paddingLeft: '20px'
        }}>
          <li>Create and manage company workspace</li>
          <li>Invite and manage team members</li>
          <li>Control billing and subscription settings</li>
          <li>Set company-wide preferences and policies</li>
        </ul>
      </motion.div>

      {/* Form */}
      <div style={{ marginBottom: '32px' }}>
        {/* Company Name */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '16px',
            fontWeight: 600,
            color: colors.gray900,
            marginBottom: '8px'
          }}>
            Company Name *
          </label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            placeholder="Enter your company name"
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '16px',
              border: `2px solid ${errors.companyName ? colors.error : colors.gray200}`,
              borderRadius: '12px',
              outline: 'none',
              transition: 'all 0.2s ease',
              backgroundColor: colors.white,
              boxSizing: 'border-box'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = colors.accent;
              e.target.style.boxShadow = `0 0 0 4px ${colors.accentLight}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = errors.companyName ? colors.error : colors.gray200;
              e.target.style.boxShadow = 'none';
            }}
          />
          {errors.companyName && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                color: colors.error,
                fontSize: '14px',
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <AlertCircle size={16} />
              {errors.companyName}
            </motion.div>
          )}
        </div>

        {/* Company Size */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '16px',
            fontWeight: 600,
            color: colors.gray900,
            marginBottom: '12px'
          }}>
            Company Size *
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px'
          }}>
            {companySizeOptions.map((option) => (
              <motion.button
                key={option.value}
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleInputChange('companySize', option.value)}
                style={{
                  padding: '16px',
                  border: `2px solid ${formData.companySize === option.value ? colors.accent : colors.gray200}`,
                  borderRadius: '12px',
                  background: formData.companySize === option.value ? colors.accentLight : colors.white,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>{option.icon}</span>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: formData.companySize === option.value ? colors.accent : colors.gray700
                  }}>
                    {option.label}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
          {errors.companySize && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                color: colors.error,
                fontSize: '14px',
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <AlertCircle size={16} />
              {errors.companySize}
            </motion.div>
          )}
        </div>

        {/* Industry */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '16px',
            fontWeight: 600,
            color: colors.gray900,
            marginBottom: '12px'
          }}>
            Industry *
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px'
          }}>
            {industryOptions.map((option) => (
              <motion.button
                key={option.value}
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleInputChange('industry', option.value)}
                style={{
                  padding: '16px',
                  border: `2px solid ${formData.industry === option.value ? colors.accent : colors.gray200}`,
                  borderRadius: '12px',
                  background: formData.industry === option.value ? colors.accentLight : colors.white,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>{option.icon}</span>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: formData.industry === option.value ? colors.accent : colors.gray700
                  }}>
                    {option.label}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
          {errors.industry && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                color: colors.error,
                fontSize: '14px',
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <AlertCircle size={16} />
              {errors.industry}
            </motion.div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {apiError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: colors.errorLight,
            border: `1px solid ${colors.error}`,
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <AlertCircle size={20} color={colors.error} />
          <span style={{ color: colors.error, fontSize: '14px' }}>
            {apiError}
          </span>
        </motion.div>
      )}

      {/* Action Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleSave}
        disabled={isLoading}
        style={{
          width: '100%',
          padding: '18px',
          background: isLoading ? colors.gray300 : `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          fontSize: '16px',
          fontWeight: 600,
          cursor: isLoading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
          ...fontStyles.medium
        }}
      >
        {isLoading ? (
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid rgba(255,255,255,0.3)',
            borderTop: '2px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        ) : (
          <>
            <span>Create Company Workspace</span>
            <ArrowRight size={20} />
          </>
        )}
      </motion.button>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </motion.div>
  );
};

export default CompanySetupStep;